import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { IngredientModificationsTypes } from '@order/utils';

import { type FilteredIngredientModifications } from '../../../types';

export const useSectionHelperText = () => {
  const { formatMessage } = useIntl();
  const isFreeAggregateMessageEnabled = useFeatureFlag(
    'CELS-4030-free-cyo-toppings-message-enabled',
  );

  return useCallback(
    (
      ingredientModifications: Partial<FilteredIngredientModifications>,
      ingredientKindName: string,
    ) => {
      const freeAggregateQuantity =
        ingredientModifications?.[
          ingredientKindName as keyof typeof ingredientModifications
        ]?.freeAggregateQuantity ?? 0;

      if (freeAggregateQuantity > 0 && isFreeAggregateMessageEnabled) {
        return formatMessage(messages.cyoToppingsHelperText, {
          quantity: freeAggregateQuantity,
        });
      }

      if (ingredientKindName === IngredientModificationsTypes.Dressings) {
        return formatMessage(messages.dressingsHelperText);
      }
    },
    [isFreeAggregateMessageEnabled, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  cyoToppingsHelperText: {
    defaultMessage: `{quantity, select,
      1 {First one is included}
      2 {First two are included}
      3 {First three are included}
      4 {First four are included}
      5 {First five are included}
      6 {First six are included}
      7 {First seven are included}
      8 {First eight are included}
      9 {First nine are included}
      10 {First ten are included}
      other {Several are included}
    }`,
    description:
      'Customization | Selected Ingredients | Toppings | Helper text',
  },
  dressingsHelperText: {
    defaultMessage: 'Portions + details on next page',
    description:
      'Customization | Selected Ingredients | Dressings | Helper text',
  },
});
